import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { UrgencyDate } from '../_shared/components/UrgencyDate';
import { contactLabel } from '../_shared/constants/contactTypes';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { contactHooks } from '../Registries/hooks';
import { correctiveActionHooks, useResolveCorrectiveAction, useVerifyCorrectiveAction, useWithdrawCorrectiveAction } from './hooks';
const CA_STATE_VARIANT = {
    Open: 'warning',
    'In Progress': 'info',
    Resolved: 'info',
    Verified: 'success',
    Cancelled: 'neutral'
};
// The corrective-action workflow (Audits guide): each failed checklist item
// gets an action, created inline on the checklist row (the note is the action,
// the owner rides beside it). This list owns the resolution lifecycle: the
// section owner resolves with a note; the auditor then verifies (accept → the
// linked item flips to Pass), sends it back for rework, or withdraws it.
export function AuditCorrectiveActions({ auditId, readOnly, canManageSelfAssessment = true }) {
    const tenantId = useTenantId();
    const { user } = useAuth();
    const contactsQuery = contactHooks.useList({
        companyId: tenantId
    });
    const { data, isLoading } = correctiveActionHooks.useList({
        auditId
    });
    const resolve = useResolveCorrectiveAction();
    const verify = useVerifyCorrectiveAction();
    const withdraw = useWithdrawCorrectiveAction();
    const contacts = contactsQuery.data?.items ?? [];
    // The contact the current user maps to (via platform_user_id), if any — used to
    // tell whether the caller IS a corrective action's assigned owner. Undefined
    // when the user has no matching contact; the Resolve gate fails open on that so
    // it never hides Resolve from a legitimate owner we simply couldn't identify.
    const myContactId = user?.id ? contacts.find((c)=>c.platformUserId === user.id)?.id : undefined;
    const [resolving, setResolving] = useState(null);
    const [rejecting, setRejecting] = useState(null);
    const [withdrawing, setWithdrawing] = useState(null);
    const actions = data?.items ?? [];
    const contactById = useMemo(()=>{
        const m = new Map();
        for (const c of contacts)m.set(c.id, c);
        return m;
    }, [
        contacts
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            isLoading ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Loading corrections…"
            }) : actions.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "No corrections on this audit. Failed checklist items raise one automatically — the owner resolves it, then the auditor verifies before the audit can close."
            }) : /*#__PURE__*/ _jsx("div", {
                className: "space-y-2",
                children: actions.map((ca)=>{
                    const owner = contactById.get(ca.assignedContactId);
                    const ownerName = owner ? contactLabel(owner) : ca.assignedTo || 'Unassigned';
                    return /*#__PURE__*/ _jsx("div", {
                        className: "rounded-md border p-3",
                        "data-testid": Testids.deliveryCompliance.caRow,
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-start justify-between gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "min-w-0",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm",
                                            children: ca.description
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mt-1 text-xs text-muted-foreground",
                                            children: [
                                                ownerName,
                                                " · due",
                                                ' ',
                                                /*#__PURE__*/ _jsx(UrgencyDate, {
                                                    value: ca.dueDate,
                                                    done: ca.state === 'Verified' || ca.state === 'Cancelled'
                                                })
                                            ]
                                        }),
                                        ca.resolutionNote ? /*#__PURE__*/ _jsx("p", {
                                            className: "mt-1 text-xs text-muted-foreground",
                                            children: ca.resolutionNote
                                        }) : null
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex shrink-0 items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: CA_STATE_VARIANT[ca.state] ?? 'neutral',
                                            children: ca.state
                                        }),
                                        !readOnly && (ca.state === 'Open' || ca.state === 'In Progress') && // Resolve is the assigned owner's action (or an auditor's).
                                        // Hide it from a caller we can positively identify as neither,
                                        // so they don't get a guaranteed PermissionDenied; fail open
                                        // when we can't identify them (myContactId undefined).
                                        (canManageSelfAssessment || !myContactId || ca.assignedContactId === myContactId) ? /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "secondary",
                                            onClick: ()=>setResolving(ca),
                                            "data-testid": Testids.deliveryCompliance.caResolve,
                                            children: "Resolve"
                                        }) : null,
                                        !readOnly && canManageSelfAssessment && ca.state === 'Resolved' ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    disabled: verify.isPending,
                                                    onClick: ()=>verify.mutate({
                                                            correctiveActionId: ca.id,
                                                            accepted: true,
                                                            verifierNotes: ''
                                                        }),
                                                    "data-testid": Testids.deliveryCompliance.caVerify,
                                                    children: "Verify"
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    variant: "secondary",
                                                    onClick: ()=>setRejecting(ca),
                                                    "data-testid": Testids.deliveryCompliance.caReject,
                                                    children: "Send back"
                                                })
                                            ]
                                        }) : null,
                                        !readOnly && canManageSelfAssessment && (ca.state === 'Open' || ca.state === 'In Progress' || ca.state === 'Resolved') ? /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "destructive",
                                            onClick: ()=>setWithdrawing(ca),
                                            "data-testid": Testids.deliveryCompliance.caWithdraw,
                                            children: "Withdraw"
                                        }) : null
                                    ]
                                })
                            ]
                        })
                    }, ca.id);
                })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: resolving !== null,
                onOpenChange: (o)=>o ? undefined : setResolving(null),
                title: "Resolve Correction",
                fields: [
                    {
                        kind: 'textarea',
                        name: 'note',
                        label: 'Resolution note',
                        rows: 3
                    }
                ],
                defaultValues: {
                    note: ''
                },
                onSubmit: async (v)=>{
                    if (resolving) {
                        await resolve.mutateAsync({
                            correctiveActionId: resolving.id,
                            resolutionNote: v.note
                        });
                    }
                    setResolving(null);
                },
                isSubmitting: resolve.isPending,
                submitLabel: "Mark Resolved"
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: rejecting !== null,
                onOpenChange: (o)=>o ? undefined : setRejecting(null),
                title: "Send Back for Rework",
                fields: [
                    {
                        kind: 'textarea',
                        name: 'note',
                        label: 'What still needs to change?',
                        rows: 3
                    }
                ],
                defaultValues: {
                    note: ''
                },
                onSubmit: async (v)=>{
                    if (rejecting) {
                        await verify.mutateAsync({
                            correctiveActionId: rejecting.id,
                            accepted: false,
                            verifierNotes: v.note
                        });
                    }
                    setRejecting(null);
                },
                isSubmitting: verify.isPending,
                submitLabel: "Send Back"
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: withdrawing !== null,
                onOpenChange: (o)=>o ? undefined : setWithdrawing(null),
                title: "Withdraw Correction",
                fields: [
                    {
                        kind: 'textarea',
                        name: 'note',
                        label: 'Why is this being withdrawn?',
                        rows: 3
                    }
                ],
                defaultValues: {
                    note: ''
                },
                onSubmit: async (v)=>{
                    if (withdrawing) {
                        await withdraw.mutateAsync({
                            correctiveActionId: withdrawing.id,
                            reason: v.note
                        });
                    }
                    setWithdrawing(null);
                },
                isSubmitting: withdraw.isPending,
                submitLabel: "Withdraw"
            })
        ]
    });
}
